/**
 * Connect Stedi Step
 *
 * First step in billing setup wizard - connects organization to Stedi.
 * User enters their Stedi API key to establish the connection.
 *
 * Uses provider routes (/v2/provider/stedi/*) which require billing_administrator role.
 */

import { useState } from 'react';
import { useStediConnectionProvider, useStediStatusProvider } from '@features/stedi-connection';
import { CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Link,
  TextField,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';

import { extractApiErrorMessage } from '../../utils/errorUtils';

interface ConnectStediStepProps {
  onNext: () => void;
  onConnected: () => void;
}

export function ConnectStediStep({ onNext, onConnected }: ConnectStediStepProps) {
  const { enqueueSnackbar } = useSnackbar();

  const [apiKey, setApiKey] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Use provider hooks - no organizationId needed (uses authenticated user's org)
  const { status, isConnected, refetch: refetchStatus } = useStediStatusProvider();
  const { connect, connectLoading, connectError, connectSuccess } = useStediConnectionProvider();

  // If already connected, show success message
  const alreadyConnected = isConnected && status?.connected;

  const handleConnect = async () => {
    if (!apiKey.trim()) {
      setError('Please enter your Stedi API key');
      return;
    }

    setError(null);

    try {
      await connect({
        connectStediRequest: {
          api_key: apiKey.trim(),
        },
      }).unwrap();

      // Refetch status to confirm connection
      await refetchStatus();
      enqueueSnackbar('Connected successfully!', { variant: 'success' });
      onConnected();
    } catch (err: unknown) {
      const errorMessage = extractApiErrorMessage(
        err,
        'The API key doesn\'t appear to be valid. Please check that you copied the full key from Stedi.',
      );
      setError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: 'error', autoHideDuration: 10000 });
    }
  };

  const handleContinue = () => {
    onNext();
  };

  if (alreadyConnected) {
    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <CheckCircleIcon color="success" sx={{ fontSize: 48, mr: 2 }} />
          <Box>
            <Typography variant="h6">Connected Successfully</Typography>
            <Typography variant="body2" color="text.secondary">
              Your practice is connected to the clearinghouse
            </Typography>
          </Box>
        </Box>

        {status?.account_label && (
          <Alert severity="success" sx={{ mb: 3 }}>
            Connected to: <strong>{status.account_label}</strong>
            {status.mode && ` (${status.mode})`}
          </Alert>
        )}

        <Button variant="contained" onClick={handleContinue} fullWidth>
          Continue to Next Step
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Connect Your Clearinghouse
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }} component="p">
        JotPsych partners with Stedi, a healthcare clearinghouse, to route your claims to
        insurance companies. Enter your Stedi API key below to connect.
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }} component="p">
        Don't have a Stedi account?{' '}
        <Link href="https://www.stedi.com" target="_blank" rel="noopener noreferrer">
          Create a free account
        </Link>{' '}
        and install the JotPsych app to get your API key.
      </Typography>

      <Box component="form" sx={{ mt: 3 }}>
        <TextField
          label="Stedi API Key"
          type="password"
          value={apiKey}
          onChange={e => setApiKey(e.target.value)}
          fullWidth
          required
          error={!!error}
          helperText={error || 'Your API key will be stored securely'}
          disabled={connectLoading}
          autoFocus
          sx={{ mb: 3 }}
        />

        {connectError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {extractApiErrorMessage(
              connectError,
              'The API key doesn\'t appear to be valid. Please check that you copied the full key from Stedi.',
            )}
          </Alert>
        )}

        {connectSuccess && (
          <Alert severity="success" sx={{ mb: 3 }}>
            Connected successfully! Click continue to proceed.
          </Alert>
        )}

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            onClick={connectSuccess ? handleContinue : handleConnect}
            disabled={connectLoading}
            fullWidth
            startIcon={connectLoading ? <CircularProgress size={20} /> : undefined}
          >
            {connectLoading ? 'Connecting...' : connectSuccess ? 'Continue' : 'Connect'}
          </Button>
        </Box>
      </Box>

      <Alert severity="info" sx={{ mt: 3 }}>
        <Typography variant="caption" component="div">
          <strong>How to get your API key:</strong>
        </Typography>
        <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
          1. Sign in to your Stedi account
        </Typography>
        <Typography variant="caption" component="div">
          2. Go to Apps → JotPsych → Install
        </Typography>
        <Typography variant="caption" component="div">
          3. Copy the API key shown during installation
        </Typography>
        <Typography variant="caption" component="div" sx={{ mt: 1, fontWeight: 600 }}>
          The API key is only shown once - make sure to copy it!
        </Typography>
      </Alert>
    </Box>
  );
}
